import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
    Mail, Search, Plus, Send, Eye, Clock, CheckCheck,
    AlertCircle, Grid, List, FileText, Reply,
    Users, Trash2, Edit, Link2, Unlink, RefreshCw, Inbox, ChevronLeft, ChevronRight, Shield
} from 'lucide-react';
import { useCommunicationStore } from '../../store/communicationStore';
import { EmailLog, EmailTemplate, GmailEmail, communicationService } from '../../services/communication.service';
import { toast } from 'sonner';
import ComposeEmailModal from './ComposeEmailModal';
import AddTemplateModal from './AddTemplateModal';
import ViewEmailModal from './ViewEmailModal';

const CommunicationHubPage: React.FC = () => {
    const {
        emails,
        templates,
        stats,
        isLoading,
        fetchEmails,
        fetchTemplates,
        fetchStats,
        searchTerm,
        setSearchTerm,
        filterStatus,
        setFilterStatus,
        viewMode,
        setViewMode,
        getFilteredEmails,
        updateEmailStatus,
        deleteTemplate,
    } = useCommunicationStore();

    const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);
    const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
    const [activeTab, setActiveTab] = useState<'emails' | 'templates' | 'gmail'>('emails');

    // Gmail state
    const [gmailConnected, setGmailConnected] = useState(false);
    const [gmailEmail, setGmailEmail] = useState<string | undefined>();
    const [gmailEmails, setGmailEmails] = useState<GmailEmail[]>([]);
    const [gmailLoading, setGmailLoading] = useState(false);
    const [selectedGmailEmail, setSelectedGmailEmail] = useState<GmailEmail | null>(null);
    const [isGmailViewModalOpen, setIsGmailViewModalOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const location = useLocation();

    // State to handle pre-fill data from navigation (partnerships and contracts)
    const [preselectedPartnership, setPreselectedPartnership] = useState<string | undefined>();
    const [prefilledSubject, setPrefilledSubject] = useState<string | undefined>();
    const [prefilledBody, setPrefilledBody] = useState<string | undefined>();
    const [prefilledTo, setPrefilledTo] = useState<string[] | undefined>();

    // Pagination state
    const ITEMS_PER_PAGE = 10;
    const [emailPage, setEmailPage] = useState(1);
    const [templatePage, setTemplatePage] = useState(1);
    const [gmailPage, setGmailPage] = useState(1);

    useEffect(() => {
        fetchEmails();
        fetchTemplates();
        fetchStats();
        checkGmailStatus();
    }, [fetchEmails, fetchTemplates, fetchStats]);

    // Check if redirected from Gmail OAuth
    useEffect(() => {
        const gmailParam = searchParams.get('gmail');
        if (gmailParam === 'connected') {
            toast.success('Gmail connected successfully!');
            checkGmailStatus();
            setActiveTab('gmail');
        } else if (gmailParam === 'error') {
            toast.error('Failed to connect Gmail. Please try again.');
        }
    }, [searchParams]);

    // Handle navigation state for composing email with pre-selected partnership or contract
    useEffect(() => {
        const state = location.state as {
            composeForPartnership?: { id: string | number };
            composeForContract?: {
                partnershipId?: string;
                recipientEmail?: string;
                subject: string;
                body: string;
            };
        } | null;

        if (state?.composeForPartnership) {
            setPreselectedPartnership(String(state.composeForPartnership.id));
            setIsComposeModalOpen(true);
            // Clear the state to prevent re-opening on page refresh
            window.history.replaceState({}, document.title);
        } else if (state?.composeForContract) {
            if (state.composeForContract.partnershipId) {
                setPreselectedPartnership(state.composeForContract.partnershipId);
            }
            if (state.composeForContract.recipientEmail) {
                setPrefilledTo([state.composeForContract.recipientEmail]);
            }
            setPrefilledSubject(state.composeForContract.subject);
            setPrefilledBody(state.composeForContract.body);
            setIsComposeModalOpen(true);
            // Clear the state to prevent re-opening on page refresh
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const checkGmailStatus = async () => {
        try {
            const status = await communicationService.getGmailStatus();
            setGmailConnected(status.connected);
            setGmailEmail(status.email);
            if (status.connected) {
                fetchGmailEmails();
            }
        } catch (error) {
            console.error('Failed to check Gmail status:', error);
        }
    };

    const fetchGmailEmails = async () => {
        try {
            setGmailLoading(true);
            const emails = await communicationService.getGmailEmails(30);
            console.log('Frontend received emails:', emails?.length, emails);
            setGmailEmails(emails);
        } catch (error) {
            console.error('Failed to fetch Gmail emails:', error);
        } finally {
            setGmailLoading(false);
        }
    };

    const handleConnectGmail = async () => {
        try {
            const authUrl = await communicationService.getGmailAuthUrl();
            window.location.href = authUrl;
        } catch (error) {
            toast.error('Failed to connect Gmail');
        }
    };

    const handleDisconnectGmail = async () => {
        if (!window.confirm('Disconnect your Gmail account?')) return;
        try {
            await communicationService.disconnectGmail();
            setGmailConnected(false);
            setGmailEmail(undefined);
            setGmailEmails([]);
        } catch (error) {
            toast.error('Failed to disconnect Gmail');
        }
    };

    const filteredEmails = getFilteredEmails();

    // Pagination helper
    const paginate = <T,>(items: T[], page: number) => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;
        return items.slice(start, end);
    };
    const getTotalPages = (total: number) => Math.ceil(total / ITEMS_PER_PAGE);

    // Paginated data
    const paginatedEmails = paginate(filteredEmails, emailPage);
    const paginatedTemplates = paginate(templates, templatePage);
    const paginatedGmailEmails = paginate(gmailEmails, gmailPage);

    // Reset page when data changes
    useEffect(() => { setEmailPage(1); }, [filterStatus, searchTerm]);
    useEffect(() => { setTemplatePage(1); }, [templates.length]);
    useEffect(() => { setGmailPage(1); }, [gmailEmails.length]);

    // Format date
    const formatDate = (dateString: string) => {
        if (!dateString) return 'Unknown';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Unknown';

        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHrs < 24) return `${diffHrs}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Get status badge styles and icon
    const getStatusInfo = (status: EmailLog['status']) => {
        const config: Record<string, { style: string; icon: React.ReactNode; label: string }> = {
            sent: {
                style: 'from-blue-400 to-blue-600 text-white',
                icon: <Send className="w-3 h-3" />,
                label: 'Sent',
            },
            replied: {
                style: 'from-green-400 to-green-600 text-white',
                icon: <CheckCheck className="w-3 h-3" />,
                label: 'Replied',
            },
            bounced: {
                style: 'from-red-400 to-red-600 text-white',
                icon: <AlertCircle className="w-3 h-3" />,
                label: 'Bounced',
            },
            scheduled: {
                style: 'from-orange-400 to-orange-600 text-white',
                icon: <Clock className="w-3 h-3" />,
                label: 'Scheduled',
            },
        };
        return config[status] || config.sent;
    };

    // Category badge color
    const getCategoryColor = (category: string) => {
        const colors: Record<string, string> = {
            outreach: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
            follow_up: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
            proposal: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
            contract: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
            invoice: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
            thank_you: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
        };
        return colors[category] || colors.outreach;
    };

    const handleViewEmail = (email: EmailLog) => {
        setSelectedEmail(email);
        setIsViewModalOpen(true);
    };

    const handleMarkAsReplied = async (email: EmailLog) => {
        await updateEmailStatus(email.id, 'replied');
    };

    const handleEditTemplate = (template: EmailTemplate) => {
        setEditingTemplate(template);
        setIsTemplateModalOpen(true);
    };

    const handleDeleteTemplate = async (template: EmailTemplate) => {
        if (window.confirm(`Delete template "${template.name}"?`)) {
            await deleteTemplate(template.id);
        }
    };

    // Pagination Controls Component
    const PaginationControls = ({
        currentPage,
        totalItems,
        setPage
    }: {
        currentPage: number;
        totalItems: number;
        setPage: (page: number) => void;
    }) => {
        const totalPages = getTotalPages(totalItems);
        if (totalPages <= 1) return null;

        return (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                    Page {currentPage} of {totalPages} ({totalItems} items)
                </span>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                    <button
                        onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                </div>
            </div>
        );
    };

    const statuses = ['all', 'sent', 'replied', 'bounced', 'scheduled'];

    if (isLoading && emails.length === 0) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Communication Hub</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your email outreach and communication templates</p>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-4 gap-4 w-full max-w-2xl">
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Sent</div>
                        <div className="text-xl font-bold text-gray-900 dark:text-white">{stats?.totalSent || 0}</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Opened</div>
                        <div className="text-xl font-bold text-purple-600">{stats?.totalOpened || 0}</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Replied</div>
                        <div className="text-xl font-bold text-green-600">{stats?.totalReplied || 0}</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Rate</div>
                        <div className="text-xl font-bold text-orange-600">{stats?.replyRate || 0}%</div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation and Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Tabs */}
                    <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                        <button
                            onClick={() => setActiveTab('emails')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'emails'
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                        >
                            <Mail className="w-4 h-4" />
                            Emails
                        </button>
                        <button
                            onClick={() => setActiveTab('templates')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'templates'
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                        >
                            <FileText className="w-4 h-4" />
                            Templates
                        </button>
                        <button
                            onClick={() => setActiveTab('gmail')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'gmail'
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                        >
                            <Inbox className="w-4 h-4" />
                            Gmail
                            {gmailConnected && <span className="w-2 h-2 bg-green-500 rounded-full"></span>}
                        </button>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 flex-1 max-w-2xl">
                        {/* Search */}
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                            <input
                                type="text"
                                placeholder={activeTab === 'emails' ? "Search emails..." : "Search templates..."}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
                            />
                        </div>

                        {/* Status Filter (only for emails) */}
                        {activeTab === 'emails' && (
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors capitalize"
                            >
                                {statuses.map(status => (
                                    <option key={status} value={status} className="capitalize">
                                        {status === 'all' ? 'All Status' : status}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        {activeTab === 'emails' && (
                            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'grid'
                                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                        }`}
                                >
                                    <Grid className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list'
                                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                        }`}
                                >
                                    <List className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        <button
                            onClick={() => {
                                if (activeTab === 'emails') {
                                    setIsComposeModalOpen(true);
                                } else {
                                    setEditingTemplate(null);
                                    setIsTemplateModalOpen(true);
                                }
                            }}
                            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            <Plus className="w-4 h-4" />
                            {activeTab === 'emails' ? 'Compose' : 'New Template'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            {activeTab === 'emails' ? (
                /* Emails Display */
                <>
                    {filteredEmails.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                                <Mail className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No emails yet</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                                Start your outreach by composing your first email.
                            </p>
                            <button
                                onClick={() => setIsComposeModalOpen(true)}
                                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                                Compose Email
                            </button>
                        </div>
                    ) : viewMode === 'list' ? (
                        /* List View */
                        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 overflow-hidden">
                            <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                {paginatedEmails.map((email) => {
                                    const statusInfo = getStatusInfo(email.status);
                                    return (
                                        <div
                                            key={email.id}
                                            className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                            onClick={() => handleViewEmail(email)}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r ${statusInfo.style}`}>
                                                            {statusInfo.icon}
                                                            {statusInfo.label}
                                                        </span>
                                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                                            {formatDate(email.sentAt)}
                                                        </span>
                                                    </div>
                                                    <p className="font-medium text-gray-900 dark:text-white truncate">
                                                        {email.subject}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                                        To: {email.to.join(', ')}
                                                        {email.partnership && (
                                                            <span className="ml-2 text-primary-600 dark:text-primary-400">
                                                                • {email.partnership.brand}
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {email.status !== 'replied' && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMarkAsReplied(email);
                                                            }}
                                                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all"
                                                            title="Mark as replied"
                                                        >
                                                            <Reply className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewEmail(email);
                                                        }}
                                                        className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <PaginationControls currentPage={emailPage} totalItems={filteredEmails.length} setPage={setEmailPage} />
                        </div>
                    ) : (
                        /* Grid View */
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {paginatedEmails.map((email) => {
                                    const statusInfo = getStatusInfo(email.status);
                                    return (
                                        <div
                                            key={email.id}
                                            className="group bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200 overflow-hidden cursor-pointer"
                                            onClick={() => handleViewEmail(email)}
                                        >
                                            <div className="p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r ${statusInfo.style}`}>
                                                        {statusInfo.icon}
                                                        {statusInfo.label}
                                                    </span>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {formatDate(email.sentAt)}
                                                    </span>
                                                </div>
                                                <h3 className="font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                                    {email.subject}
                                                </h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate mb-3">
                                                    To: {email.to[0]}{email.to.length > 1 && ` +${email.to.length - 1}`}
                                                </p>
                                                {email.partnership && (
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Users className="w-3.5 h-3.5 text-gray-400" />
                                                        <span className="text-primary-600 dark:text-primary-400 font-medium">
                                                            {email.partnership.brand}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <PaginationControls currentPage={emailPage} totalItems={filteredEmails.length} setPage={setEmailPage} />
                        </>
                    )}
                </>
            ) : activeTab === 'templates' ? (
                /* Templates Display */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {templates.length === 0 ? (
                        <div className="col-span-full bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                                <FileText className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No templates yet</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                                Create reusable email templates for faster communication.
                            </p>
                            <button
                                onClick={() => {
                                    setEditingTemplate(null);
                                    setIsTemplateModalOpen(true);
                                }}
                                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                                Create Template
                            </button>
                        </div>
                    ) : (
                        paginatedTemplates.map((template) => (
                            <div
                                key={template.id}
                                className={`group bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border transition-all duration-200 overflow-hidden ${template.isBuiltIn
                                        ? 'border-primary-200 dark:border-primary-700 ring-1 ring-primary-100 dark:ring-primary-900/30'
                                        : 'border-gray-100 dark:border-gray-700 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)]'
                                    }`}
                            >
                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${template.isBuiltIn
                                                ? 'bg-gradient-to-br from-primary-500 to-purple-600'
                                                : 'bg-gradient-to-br from-primary-500 to-indigo-600'
                                            }`}>
                                            {template.isBuiltIn ? <Shield className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {template.isBuiltIn && (
                                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                                                    Built-in
                                                </span>
                                            )}
                                            <span className={`px-2.5 py-1 text-xs font-medium rounded-lg capitalize ${getCategoryColor(template.category)}`}>
                                                {template.category.replace('_', ' ')}
                                            </span>
                                        </div>
                                    </div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                        {template.name}
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
                                        {template.subject}
                                    </p>
                                    {template.variables.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {template.variables.slice(0, 3).map((v) => (
                                                <span key={v} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">
                                                    {`{{${v}}}`}
                                                </span>
                                            ))}
                                            {template.variables.length > 3 && (
                                                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">
                                                    +{template.variables.length - 3}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="px-5 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-100 dark:border-gray-600 flex items-center justify-between">
                                    <button
                                        onClick={() => {
                                            setEditingTemplate(null);
                                            // Use template for compose
                                            setIsComposeModalOpen(true);
                                        }}
                                        className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                                    >
                                        Use Template
                                    </button>
                                    {/* Only show edit/delete for non-builtin templates */}
                                    {!template.isBuiltIn && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => handleEditTemplate(template)}
                                                className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteTemplate(template)}
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                    <PaginationControls currentPage={templatePage} totalItems={templates.length} setPage={setTemplatePage} />
                </div>
            ) : (
                /* Gmail Tab */
                <div className="space-y-6">
                    {/* Gmail Connection Status */}
                    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${gmailConnected ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                                    <Mail className={`w-6 h-6 ${gmailConnected ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white">
                                        {gmailConnected ? 'Gmail Connected' : 'Connect Your Gmail'}
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {gmailConnected ? gmailEmail : 'Send and receive emails directly from your Gmail account'}
                                    </p>
                                </div>
                            </div>
                            {gmailConnected ? (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={fetchGmailEmails}
                                        disabled={gmailLoading}
                                        className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${gmailLoading ? 'animate-spin' : ''}`} />
                                        Refresh
                                    </button>
                                    <button
                                        onClick={handleDisconnectGmail}
                                        className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                                    >
                                        <Unlink className="w-4 h-4" />
                                        Disconnect
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={handleConnectGmail}
                                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                                >
                                    <Link2 className="w-4 h-4" />
                                    Connect Gmail
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Gmail Inbox */}
                    {gmailConnected && (
                        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                    <Inbox className="w-5 h-5" />
                                    Inbox ({gmailEmails.length})
                                </h3>
                            </div>
                            {gmailLoading ? (
                                <div className="p-12 text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                                    <p className="text-gray-500 dark:text-gray-400 mt-4">Loading emails...</p>
                                </div>
                            ) : gmailEmails.length === 0 ? (
                                <div className="p-12 text-center">
                                    <Mail className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                    <p className="text-gray-500 dark:text-gray-400">No emails found</p>
                                </div>
                            ) : (
                                <>
                                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {paginatedGmailEmails.map((email) => (
                                            <div
                                                key={email.id}
                                                onClick={() => {
                                                    setSelectedGmailEmail(email);
                                                    setIsGmailViewModalOpen(true);
                                                }}
                                                className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer ${!email.isRead ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-2 h-2 rounded-full mt-2 ${!email.isRead ? 'bg-blue-500' : 'bg-transparent'}`}></div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between gap-2 mb-1">
                                                            <span className={`text-sm truncate ${!email.isRead ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                                                                {email.from}
                                                            </span>
                                                            <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                                                                {formatDate(typeof email.date === 'string' ? email.date : email.date.toISOString())}
                                                            </span>
                                                        </div>
                                                        <p className={`truncate ${!email.isRead ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                                                            {email.subject || '(No subject)'}
                                                        </p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                            {email.snippet}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <PaginationControls currentPage={gmailPage} totalItems={gmailEmails.length} setPage={setGmailPage} />
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Modals */}
            <ComposeEmailModal
                isOpen={isComposeModalOpen}
                onClose={() => {
                    setIsComposeModalOpen(false);
                    setPreselectedPartnership(undefined);
                    setPrefilledSubject(undefined);
                    setPrefilledBody(undefined);
                    setPrefilledTo(undefined);
                }}
                {...(preselectedPartnership && { preselectedPartnership })}
                {...(prefilledSubject && { prefilledSubject })}
                {...(prefilledBody && { prefilledBody })}
                {...(prefilledTo && { prefilledTo })}
            />

            <AddTemplateModal
                isOpen={isTemplateModalOpen}
                onClose={() => {
                    setIsTemplateModalOpen(false);
                    setEditingTemplate(null);
                }}
                editingTemplate={editingTemplate}
            />

            {/* Email View Modal - handles both sent emails and Gmail emails */}
            <ViewEmailModal
                isOpen={isViewModalOpen || isGmailViewModalOpen}
                onClose={() => {
                    setIsViewModalOpen(false);
                    setIsGmailViewModalOpen(false);
                    setSelectedEmail(null);
                    setSelectedGmailEmail(null);
                }}
                email={selectedEmail || undefined}
                gmailEmail={selectedGmailEmail || undefined}
            />
        </div>
    );
};

export default CommunicationHubPage;
